import { useQuery } from "@tanstack/react-query";
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { useAuth } from "./useAuth";
import type { CourseWithTrail } from "@/types";

export type { CourseWithTrail as Course };

export const useCourses = () => {
  const { user } = useAuth();
  const { supabase } = useSupabaseConnection();
  
  return useQuery({
    queryKey: ["courses", user?.id || "anonymous"],
    queryFn: async (): Promise<CourseWithTrail[]> => {
      try {
        // Load static course data from JSON file
        const response = await fetch('/courses.json');
        if (!response.ok) {
          throw new Error(`Failed to fetch courses.json: ${response.status}`);
        }
        
        const staticCourses: CourseWithTrail[] = await response.json();
        
        // Fetch real-time recommend_count from database
        const { data: courseRecommendCounts, error: countError } = await supabase
          .from("courses")
          .select("course_id, recommend_count");

        if (countError) {
          console.warn("Failed to fetch recommend counts:", countError.message);
          // Continue with static recommend_count if database query fails
        }

        // Create a map for quick lookup of recommend counts
        const recommendCountMap = new Map(
          courseRecommendCounts?.map(course => [course.course_id, course.recommend_count]) || []
        );

        // If user is logged in, fetch recommendation data separately
        if (user) {
          const { data: recommendations, error } = await supabase
            .from("course_recommendations")
            .select("course_id")
            .eq("user_id", user.id);

          if (error) {
            console.warn("Failed to fetch recommendations:", error.message);
            // Continue without recommendations if there's an error
            return staticCourses.map(course => ({
              ...course,
              recommend_count: recommendCountMap.get(course.course_id) || course.recommend_count || 0,
              is_recommended: false
            }));
          }

          const recommendedCourseIds = new Set(
            recommendations?.map(rec => rec.course_id) || []
          );

          return staticCourses.map(course => ({
            ...course,
            recommend_count: recommendCountMap.get(course.course_id) || course.recommend_count || 0,
            is_recommended: recommendedCourseIds.has(course.course_id)
          }));
        }

        // For non-logged-in users, return static data with real-time recommend counts
        return staticCourses.map(course => ({
          ...course,
          recommend_count: recommendCountMap.get(course.course_id) || course.recommend_count || 0,
          is_recommended: false
        }));
      } catch (error) {
        console.error("Error loading course data:", error);
        throw new Error(`Failed to load course data: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    },
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes since recommend_count is dynamic
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    refetchOnMount: true, // Refetch on mount to get latest recommend counts
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid too frequent requests
    refetchOnReconnect: true, // Refetch on reconnect to sync data
  });
};